/**
 * Root-level logic to instrument continuation and termination events, used in
 * calculating Learning Time.
 *
 * - CONTINUATION events represent the user continuing to interact with the
 *   page, e.g. via `mousemove` and `scroll` DOM events.
 * - TERMINATION events represent the user ceasing to interact with the page,
 *   e.g. by tabbing away.
 *
 * See ADR #144 for a more complete definition and explanation of how these
 * events are used.
 */
import debounce from "lodash.debounce";

import {RequestInfo} from "@khan/shared-core";

import {actions, store} from "./store.ts";

// Information about a DOM event we need to instrument.
interface InstrumentedEvent {
    target: EventTarget;
    // Should be a DOM event type.
    type: string;
    handler: (arg1: Event) => void;
    useCapture?: boolean;
}

/**
 * DOM event handlers will be debounced to this millisecond interval.
 * This is distinct from the Learning Time v2 CONTINUATION_GAP, but should
 * be significantly less in practice.
 */
const HANDLER_DEBOUNCE_INTERVAL_MS = 1000;

const handleTermination = debounce((event: Event) => {
    store.dispatch(actions.markTerminationConversion(Date.now()));
}, HANDLER_DEBOUNCE_INTERVAL_MS);

const handleContinuation = debounce(
    (event: Event, forceConversion = RequestInfo.isE2E) => {
        store.dispatch(
            actions.markContinuationConversion(Date.now(), forceConversion),
        );
    },
    HANDLER_DEBOUNCE_INTERVAL_MS,
);

/**
 * When the tab is defocused or refocused, send the appropriate
 * termination or continuation event.
 */
const handleVisibilityChange = debounce((event: Event) => {
    const {visibilityState} = document;
    // @ts-expect-error - TS2367 - This condition will always return 'false' since the types '"visible"' and '"prerender"' have no overlap.
    if (visibilityState === "hidden" || visibilityState === "prerender") {
        handleTermination(event);
    } else if (visibilityState === "visible") {
        handleContinuation(event, true);
    }
}, HANDLER_DEBOUNCE_INTERVAL_MS);

/**
 * List of DOM events to instrument as continuations or terminations.
 *
 * TODO(slim): Instrument `beforeunload` to fire termination events when
 * the user closes the tab or refreshes the page.
 */
const instrumentedEvents: ReadonlyArray<InstrumentedEvent> = [
    // sync-start:user-interaction 1417193960 ./apps/khanacademy/src/shared/app-shell/with-interacted-recently.tsx
    {
        target: document,
        type: "mousemove",
        handler: handleContinuation,
    },
    {
        target: document,
        type: "scroll",
        handler: handleContinuation,
        // Scroll events do not bubble, so we need to bind to the
        // capturing phase to register scrolling in modals.
        useCapture: true,
    },
    {
        target: document,
        type: "visibilitychange",
        handler: handleVisibilityChange,
    },
    // sync-end:user-interaction
];

// Avoid double-registering event listeners.
let IS_HEARTBEAT_TRACKING_ACTIVE = false;

export const init = () => {
    if (!IS_HEARTBEAT_TRACKING_ACTIVE) {
        for (const {target, type, handler, useCapture} of instrumentedEvents) {
            target.addEventListener(type, handler, useCapture);
        }
        IS_HEARTBEAT_TRACKING_ACTIVE = true;
    }
};
